import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsync } from '../../../core/hooks/useAsync';
import { getHomePageDeliveryList, getRiderSlotData } from '../api/RiderHomeApi';
import {
    HomePageDeliveryCountRequest,
    HomePageDeliveryStats,
    DEFAULT_HOME_PAGE_DELIVERY_STATS,
} from '../types/homePage';

// vroong 기준일: 06:00 ~ 익일 05:59를 하루로 봄
const getVroongToday = (): Date => {
    const now = new Date();
    if (now.getHours() < 6) {
        now.setDate(now.getDate() - 1);
    }
    return now;
};

const formatLocalDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

export const useHomePageForRider = () => {
    const [deliveryStats, setDeliveryStats] = useState<HomePageDeliveryStats>(DEFAULT_HOME_PAGE_DELIVERY_STATS);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [isFetching, setIsFetching] = useState(true);
    
    const deliveryListState = useAsync(getHomePageDeliveryList);

    const getDateDisplayText = (date: Date, daysAgo: number) => {
        if (daysAgo === 0) return '오늘';
        if (daysAgo === 1) return '어제';
        return formatLocalDate(date);
    };

    const dateOptions = useMemo(() => {
        const dates = [];
        const today = getVroongToday();
        
        for (let i = 0; i <= 2; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() - i);
            const dateString = formatLocalDate(date);
            const displayText = getDateDisplayText(date, i);
            dates.push({ value: dateString, label: displayText });
        }
        
        if (selectedDate && !dates.find(d => d.value === selectedDate)) {
            dates.unshift({ value: selectedDate, label: selectedDate });
        }
        
        return dates;
    }, [selectedDate]);

    useEffect(() => {
        if (dateOptions && dateOptions.length > 0 && !selectedDate) {
            setSelectedDate(dateOptions[0].value);
        }
    }, [dateOptions, selectedDate]);

    const selectedYyyymmdd = useMemo(() => selectedDate.replace(/-/g, ''), [selectedDate]);

    const calcSlotCountsFromHours = useCallback(
        (hourCounts: number[], slotTimeRanges: string[]): number[] => {
            return slotTimeRanges.map(range => {
                const match = range.match(/(\d{2}):(\d{2})\s*~\s*(\d{2}):(\d{2})/);
                if (!match) return 0;
                const start = parseInt(match[1], 10);
                const end = parseInt(match[3], 10);
                if (start <= end) {
                    let total = 0;
                    for (let h = start; h <= end; h++) total += hourCounts[h] ?? 0;
                    return total;
                } else {
                    let total = 0;
                    for (let h = start; h <= 23; h++) total += hourCounts[h] ?? 0;
                    for (let h = 0; h <= end; h++) total += hourCounts[h] ?? 0;
                    return total;
                }
            });
        },
        []
    );

    const fetchDeliveryData = useCallback(async () => {
        if (!selectedYyyymmdd) return;
        setIsFetching(true);

        // 슬롯 이름/시간범위는 weekly API에서, 건수는 HOUR_XX_CNT에서 직접 계산
        const slotData = await getRiderSlotData(selectedYyyymmdd);

        try {
            const requestParams: HomePageDeliveryCountRequest = {
                fromDate: selectedYyyymmdd,
            };

            const responseData = await deliveryListState.execute(requestParams);
            const deliveryData = responseData.deliveryDataList?.[0];

            if (deliveryData) {
                const totalCompleted = deliveryData.finishCnt || 0;
                const totalRejected = deliveryData.rejectCnt || 0;
                const totalRequests = totalCompleted + totalRejected + (deliveryData.assingCancelCnt || 0) + (deliveryData.deliCancelCnt || 0);
                const acceptanceRate = totalRequests > 0 ? Math.round((totalCompleted / totalRequests) * 100) : 0;

                const hourCounts: number[] = deliveryData.hourCounts?.map(Number) ?? new Array(24).fill(0);
                const slotCounts = calcSlotCountsFromHours(hourCounts, slotData.slotTimeRanges);

                setDeliveryStats({
                    completedCount: totalCompleted,
                    rejectedCount: totalRejected + (deliveryData.assingCancelCnt || 0) + (deliveryData.deliCancelCnt || 0),
                    acceptanceRate: acceptanceRate,
                    deliveryFee: deliveryData.totDeliveryAtm || 0,
                    morningLunchPeakCount: deliveryData.morTimeCnt || 0,
                    afternoonNonPeakCount: deliveryData.afterNonCnt || 0,
                    eveningPeakCount: deliveryData.nightNonCnt || 0,
                    nightPeakCount: deliveryData.nightCnt || 0,
                    foodCount: deliveryData.finishFoodCnt || 0,
                    bmartCount: deliveryData.finishBmartCnt || 0,
                    storeCount: deliveryData.finishStoreCnt || 0,
                    slotNames: slotData.slotNames,
                    slotCounts,
                    slotTimeRanges: slotData.slotTimeRanges,
                });
            } else {
                setDeliveryStats({
                    ...DEFAULT_HOME_PAGE_DELIVERY_STATS,
                    slotNames: slotData.slotNames,
                    slotCounts: slotData.slotNames.map(() => 0),
                    slotTimeRanges: slotData.slotTimeRanges,
                });
            }
        } catch (err) {
            setDeliveryStats({
                ...DEFAULT_HOME_PAGE_DELIVERY_STATS,
                slotNames: slotData.slotNames,
                slotCounts: slotData.slotNames.map(() => 0),
                slotTimeRanges: slotData.slotTimeRanges,
            });
        } finally {
            setIsFetching(false);
        }
    }, [selectedYyyymmdd, calcSlotCountsFromHours]);

    const handleDateChange = useCallback((date: string) => {
        setSelectedDate(date);
    }, []);

    const refreshData = useCallback(() => {
        fetchDeliveryData();
    }, [fetchDeliveryData]);

    useEffect(() => {
        if (selectedYyyymmdd) {
            fetchDeliveryData();
        }
    }, [selectedYyyymmdd, fetchDeliveryData]);

    return {
        deliveryStats,
        selectedDate,
        dateOptions,
        isLoading: isFetching || deliveryListState.isLoading,
        error: deliveryListState.error,
        handleDateChange,
        refreshData,
        isDataLoaded: deliveryStats.completedCount > 0 || deliveryStats.rejectedCount > 0,
    };
};
